import React from 'react';
import styled from 'styled-components';

import Logger from 'observability/Logger';
import { useBootstrap } from 'utils/bootstrap/useBootstrap';

import GoldcastMessage from './GoldcastMessage';

const LOG = new Logger('GoldcastEmbed');

const EmbedWrapper = styled.div`
    width: 100%;
    height: 100%;
`;

let goldcastSdkPromise;

const loadGoldcastSdk = () => {
    if (window?.GOLDCAST?.RenderEmbeddedWidget) {
        return Promise.resolve();
    }

    if (goldcastSdkPromise) {
        return goldcastSdkPromise;
    }

    goldcastSdkPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://embedded.sdk.goldcast.io/embedded-sdk.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = (error) => {
            script.remove();
            goldcastSdkPromise = null;
            reject(error);
        };

        document.body.appendChild(script);
    });

    return goldcastSdkPromise;
};

const GoldcastEmbed = () => {
    const initializedRef = React.useRef(false);
    const { goldcastEmbeddedServerUrl } = useBootstrap(['goldcastEmbeddedServerUrl']);
    const [loadState, setLoadState] = React.useState('loading'); // 'loading' | 'ready' | 'error'

    React.useEffect(() => {
        if (!goldcastEmbeddedServerUrl || initializedRef.current) {
            return undefined;
        }

        let isMounted = true;

        loadGoldcastSdk()
            .then(() => {
                if (!isMounted || initializedRef.current) {
                    return;
                }

                const config = {
                    embeddedServerUrl: goldcastEmbeddedServerUrl,
                };

                const widget = new window.GOLDCAST.RenderEmbeddedWidget(config);
                widget.init();
                initializedRef.current = true;
                setLoadState('ready');
            })
            .catch((error) => {
                LOG.error('Goldcast embed SDK failed to load', error);
                if (isMounted) {
                    setLoadState('error');
                }
            });

        return () => {
            isMounted = false;
            initializedRef.current = false;
        };
    }, [goldcastEmbeddedServerUrl]);

    if (!goldcastEmbeddedServerUrl || loadState === 'error') {
        return <GoldcastMessage message="Something went wrong. Please try again later." />;
    }

    return <EmbedWrapper id="gc-embedded-div" />;
};

export default GoldcastEmbed;
